import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import './AdSense.css';

/**
 * AdSense Component
 * 
 * This component creates and initializes Google AdSense ads.
 * 
 * @param {Object} props Component props
 * @param {string} props.adSlot The ad unit ID (will be used with real AdSense)
 * @param {string} props.adFormat The ad format (e.g., 'auto', 'rectangle', etc.)
 * @param {string} props.adLayout The ad layout (e.g., 'in-article', etc.)
 * @param {string} props.style Additional inline styles for the ad container
 * @param {string} props.className Additional CSS classes for the ad container
 */
const AdSense = ({
  adSlot = 'test-slot',
  adFormat = 'auto',
  adLayout = null,
  style = {},
  className = ''
}) => {
  const adRef = useRef(null);
  const initialized = useRef(false);

  useEffect(() => {
    // Only initialize this specific ad if it hasn't been initialized yet
    if (adRef.current && !initialized.current && typeof window !== 'undefined') {
      initialized.current = true;

      try {
        // Check if the ad element already has 'data-ad-status' attribute
        if (!adRef.current.getAttribute('data-ad-status')) {
          // Push to adsbygoogle only if not already initialized
          (window.adsbygoogle = window.adsbygoogle || []).push({});
        }
      } catch (error) {
        console.warn('Failed to initialize AdSense ad:', error);
      }
    }
  }, []);

  useEffect(() => {
    // Initialize the ad when the component mounts
    try {
      if (window.adsbygoogle) {
        // Ad is initialized
      }
    } catch (error) {
      console.warn('AdSense error:', error);
    }

    // For testing, we'll add a timeout to simulate ad loading
    const timer = setTimeout(() => {
      if (adRef.current) {
        adRef.current.classList.add('ad-loaded');
      }
    }, 1000);

    return () => {
      clearTimeout(timer);
    };
  }, [adSlot, adFormat, adLayout]);

  return (
    <div
      ref={adRef}
      className={`adsense-container ${className || ''}`}
      style={style}
    >
      <ins
        className="adsbygoogle"
        style={{
          display: 'block',
          textAlign: 'center',
          width: '100%',
          height: '100%',
        }}
        data-ad-layout={adLayout}
        data-ad-client={import.meta.env.VITE_GOOGLE_ADSENSE_ID || "ca-pub-2619959628596982"}
        data-ad-slot={adSlot}
        data-ad-format={adFormat}
        data-full-width-responsive="true"
      />
      <div className="ad-content">
        <div className="ad-label">Advertisement</div>
        <div className="ad-placeholder-content">
          <div className="ad-placeholder-text">Ad Space</div>
          <div className="ad-placeholder-subtext">Your ad will appear here</div>
        </div>
      </div>
    </div>
  );
};

AdSense.propTypes = {
  adSlot: PropTypes.string.isRequired,
  adFormat: PropTypes.string,
  adLayout: PropTypes.string,
  style: PropTypes.object,
  className: PropTypes.string
};

export default AdSense; 